/**
 * useChatV2 - Main chat hook for Chat V2 UI
 *
 * Uses the V1 backend APIs with a conversation-based approach.
 * This hook manages conversation creation, message sending, and SSE streaming.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'
import type { UIMessage, AgentId, ContextType, MessagePart, MessageRole, FilePart, ChartData, SourcePart, CitationType, VersionEntry, VersionInfo, ReasoningPath } from '../types'
import type { OrchestrationStatusData, OrchestrationStatusType } from '../components/messages/OrchestrationStatus'
import type { ToolProgressData } from '../components/messages/ToolProgressStatus'
import { parseToolProgressFrame } from '../utils/parseToolProgressFrame'
import { currentColorScheme } from '@/lib/colorScheme'
import {
  CHAT_V2_ENDPOINTS,
  INTUIGENCE_PARTS_HEADER,
  INTUIGENCE_PARTS_VERSION,
  VERCEL_AI_STREAM_HEADER,
  VERCEL_AI_STREAM_VERSION,
} from '../constants'
import {
  detectAgentMention,
  hasMultipleAgentMentions,
  getAgentMentionName,
} from '../utils/messageConverter'
import { getAuthToken } from '../utils/auth'
import { useChatV2Store } from './useChatV2Store'
import { config } from '@/config'
import { apiService } from '@/service/api'

import { stripInternalMetadata } from '../utils/stripInternalMetadata'
import { extractSourcesFromFrames, parseChunk, reduceFramesToParts } from './useChatV3'
import {
  WorkflowProgressPart as WorkflowProgressPartSchema,
  type UIMessagePart,
  type WorkflowProgressPart,
} from '@intuigence/response-parts'

type TimeseriesOptions = Record<string, unknown>

/**
 * W16 / TIME-198: how long to hold the `failed` tool-progress state
 * visible before clearing. Without the hold, the renderer's red-dot
 * styling never paints because the parent unmounts the line within
 * the same React batch as the failed frame. 800ms is short enough
 * that a fast retry isn't visually delayed, long enough that an
 * operator notices the failed indicator.
 */
const FAILED_HOLD_MS = 800

/**
 * Wire-level adapter — Vercel UIMessageStream parts use `input`/`output`
 * for tool calls/results; the legacy AssistantMessage renderer (which
 * still handles pure-text + tool messages per AssistantMessageRouter's
 * `hasV2TypedParts` check) reads `args`/`result`. Add both names so
 * either renderer renders correctly. `data-*` parts pass through
 * unchanged and trigger the V3 renderer via the router.
 */
function mapV3PartsToLegacyParts(parts: UIMessagePart[]): MessagePart[] {
  return parts.map((p) => {
    if (p.type === 'tool-call') {
      return {
        ...p,
        args: (p.input as Record<string, unknown> | null | undefined) ?? {},
      } as unknown as MessagePart
    }
    if (p.type === 'tool-result') {
      return {
        ...p,
        result: p.output,
      } as unknown as MessagePart
    }
    return p as unknown as MessagePart
  })
}

/**
 * Reduce a V2/UIMessageStream frame sequence to legacy `MessagePart[]`
 * for the in-flight assistant message, including `source` parts lifted
 * from the kg-agent's `data-kg-sources` snapshot. Without the source
 * append, streamed kg-agent citations (the `data-kg-sources` frame) are
 * dropped — `reduceFramesToParts` has no case for that snapshot — so
 * P&ID/document/timeseries answers render with no Source card until a
 * reload re-hydrates `Message.sources`. Pure; exported for testing.
 */
export function buildV2StreamParts(
  frames: Parameters<typeof reduceFramesToParts>[0],
): MessagePart[] {
  const legacy = mapV3PartsToLegacyParts(reduceFramesToParts(frames))
  const sourceParts = extractSourcesFromFrames(frames).map(convertToSourcePart)
  return sourceParts.length > 0 ? [...legacy, ...sourceParts] : legacy
}

function upsertWorkflowProgressPart(
  parts: MessagePart[] | undefined,
  workflowProgress: WorkflowProgressPart,
): MessagePart[] {
  // `Message.parts` is typed non-optional, but a message hydrated before this
  // feature shipped can arrive without a `parts` array; default to [] so the
  // spread/filter can't throw "Cannot spread non-iterable instance".
  return [
    ...(parts ?? []).filter((p) => p.type !== 'data-workflow-progress'),
    workflowProgress as unknown as MessagePart,
  ]
}

function preserveNonTextParts(parts: MessagePart[]): MessagePart[] {
  return parts.filter(
    (p) =>
      p.type === 'file' ||
      p.type === 'source' ||
      p.type === 'data-workflow-progress',
  )
}

/** Shape of a source object returned by the API */
interface RawAPISource {
  source_id?: string
  type?: string
  confidence?: number
  content?: string
  // Backend-supplied display fields (kg-agent `KgSourceCitation` /
  // Python `sources`). Historically omitted here, which dropped the
  // human title (e.g. "Sheet R279032010P02") on the converted path and
  // left the footer showing a bare "Source N".
  title?: string
  url?: string
  doc_id?: string
  chunk_id?: string
  page_number?: number
  entity_id?: string
  pid_source?: string
  relationship_id?: string
  tag_id?: string
  time_range_start?: string
  time_range_end?: string
}

/** Shape of a message object returned by the conversation history API */
interface RawAPIMessage {
  id: string
  role: string
  content?: string
  agent_used?: string
  chart_data?: ChartData[]
  sources?: RawAPISource[]
  parts?: UIMessagePart[]
  timestamp?: string
  feedback_value?: boolean | null
  reasoning_path?: ReasoningPath | null
  rating?: number
  follow_up_questions?: string[]
  // AGNT-556: persisted reasoning trace served by the NestJS chat
  // controller (`MessageResponseDto.thinking`). Rebuild the Thinking
  // pane from this on conversation reload — without it the pane
  // collapsed empty after navigating away and back.
  thinking?: string | null
  // AGNT-686: persisted correlation ID so the 🔍 kg-trace forensics
  // link survives page reload.
  correlation_id?: string | null
}

/**
 * Parsed result from an SSE data line.
 *
 * @internal Exported only to enable unit tests of `parseSSEData`'s
 * tool_progress branch (W16 / TIME-198). Don't rely on this type from
 * other features — the shape is intentionally tied to this hook's
 * stream-loop and may change without notice.
 */
export interface SSEParsedData {
  content?: string
  done?: boolean
  error?: string
  agent_used?: string
  stream_action?: 'append' | 'replace'
  follow_up_questions?: string[]
  charts?: string[]
  chart_data?: ChartData[]
  sources?: RawAPISource[]
  thinking?: string
  role?: string
  id?: string
  // W16 / TIME-198: ephemeral progress signal for slow tools. Surfaced
  // separately from `content` so the UI can render a status line while
  // the tool blocks the streamText loop, and clear it on terminal
  // status (`completed` / `failed`).
  toolProgress?: ToolProgressData
  // FS-676: egress-consent prompt frame (`type: 'consent_request'`) — the
  // turn would have used a public egress surface but the tenant's `ask`
  // policy has no stored verdict for this conversation yet.
  consentRequest?: { surfaces: string[] }
  workflowProgress?: WorkflowProgressPart
  // ENG-2419 PR B: V2 UI-message-stream typed parts. The backend's
  // `bundle.replaceText(finalText)` (PR #13990) emits a part-style frame
  // `{ type: 'data-text-replace', data: { text, reason } }` to swap a
  // hallucinated draft for the groundedness-regen abstain. The V1 SSE
  // path uses `stream_action: 'replace'` on a content frame; V2 carries
  // the new text under `data.text` instead.
  type?: string
  data?: { text?: string; reason?: string }
}

interface UseChatV2Options {
  contextId: string
  contextType: ContextType
  conversationId?: string
  timeseriesOptions?: TimeseriesOptions
  onError?: (error: Error) => void
}

/**
 * Build full URL from path
 */
function getUrl(path: string): string {
  const cleanBaseUrl = config.apiBaseUrl.replace(/\/+$/, '')
  const cleanPath = path.replace(/^\/+/, '')
  return `${cleanBaseUrl}/${cleanPath}`
}

/**
 * Normalize backend agent_used to ChatV2 AgentId.
 * Backend streams v1 agent strings like `chemical_engineer`.
 */
function mapBackendAgentUsedToAgentId(agentUsed: string | null | undefined): AgentId | null {
  if (!agentUsed) return null
  const key = agentUsed.trim().toLowerCase()
  const mapping: Record<string, AgentId> = {
    // Backend v1 agent ids
    'plant_support_engineer': 'pam',
    'chemical_engineer': 'aice',
    'mechanical_engineer': 'max',
    'electrical_engineer': 'ella',

    // Tool-based responses (map to PAM)
    'workspace_meta_tool': 'pam',

    // App-store-ish ids (defensive)
    'plant-support': 'pam',
    'chemical-agent': 'aice',
    'mechanical-agent': 'max',
    'electrical-agent': 'ella',

    // ChatV2 ids
    'pam': 'pam',
    'aice': 'aice',
    'max': 'max',
    'ella': 'ella',

    // Display names (defensive)
    'ai plant support engineer': 'pam',
    'ai chemical engineer': 'aice',
    'ai mechanical engineer': 'max',
    'ai electrical engineer': 'ella',
    'plant support engineer': 'pam',
    'chemical engineer': 'aice',
    'mechanical engineer': 'max',
    'electrical engineer': 'ella',
  }
  return mapping[key] || null
}

/**
 * Parse SSE data line to extract content, chart data, and sources.
 *
 * @internal Exported only for unit-test coverage of the W16 tool_progress
 * branch (TIME-198). The parser is a pure function with no React
 * surface, so testing it directly is faster and more precise than
 * driving the whole hook through a fake stream. Don't import from
 * other features — the shape is tied to this hook.
 */
export function parseSSEData(data: string): SSEParsedData {
  if (data === '[DONE]') {
    return { done: true }
  }

  try {
    const parsed = JSON.parse(data)
    const result: SSEParsedData = {}

    // Handle content - ensure it's a string, not an object
    if (parsed.content !== undefined && parsed.content !== null) {
      if (typeof parsed.content === 'string') {
        result.content = parsed.content
      } else if (typeof parsed.content === 'object') {
        // If content is an object, try to extract the actual text content
        result.content = parsed.content.content || parsed.content.text || ''
      }
    }

    if (parsed.error) {
      result.error = parsed.error
    }
    if (parsed.agent_used) {
      result.agent_used = parsed.agent_used
    }
    if (typeof parsed.stream_action === 'string') {
      result.stream_action = parsed.stream_action
    }
    if (parsed.follow_up_questions) {
      result.follow_up_questions = parsed.follow_up_questions
    }
    // Handle chart data from historian backend
    if (parsed.charts) {
      result.charts = parsed.charts
    }
    if (parsed.chart_data) {
      result.chart_data = parsed.chart_data
    }

    // Handle sources from grounding validation
    if (parsed.sources) {
      result.sources = parsed.sources
    }

    // Handle thinking from Claude Direct Mode
    if (typeof parsed.thinking === 'string') {
      result.thinking = parsed.thinking
    }

    // W16 / TIME-198: tool_progress frame — surfaced as a typed
    // ToolProgressData so the hook can drive the status-line lifecycle.
    // Closed-enum validation lives in `parseToolProgressFrame` so V1
    // (ChatInterface) and V2 (this hook) share one parser — keeping
    // them in lock-step prevents version-skew bugs.
    const tp = parseToolProgressFrame(parsed)
    if (tp) {
      result.toolProgress = tp
    }

    // FS-676: egress-consent prompt. The backend emits ONE frame at turn end
    // when a tool row carried `consentOffer` (V1 assistant envelope with
    // `content:''`, so it appends no text). Validate the surface list is a
    // non-empty string array — a malformed frame degrades to "no prompt".
    if (parsed.type === 'consent_request') {
      const surfaces = (parsed.consent_request as { surfaces?: unknown } | undefined)?.surfaces
      if (
        Array.isArray(surfaces) &&
        surfaces.length > 0 &&
        surfaces.every((s: unknown) => typeof s === 'string')
      ) {
        result.consentRequest = { surfaces: surfaces as string[] }
      }
    }

    if (parsed.type === 'data-workflow-progress') {
      const workflowProgress = WorkflowProgressPartSchema.safeParse({
        type: 'data-workflow-progress',
        ...((parsed.data as Record<string, unknown>) ?? {}),
      })
      if (workflowProgress.success) {
        result.workflowProgress = workflowProgress.data
      }
    }

    // ENG-2419 PR B: V2 UI-message-stream typed parts. `data-text-replace`
    // is emitted post-stream by `bundle.replaceText(finalText)` when the
    // groundedness regen forces a stock abstain (PR #13990 / AGNT-767).
    // The handler in the stream loop swaps `fullContent` for `data.text`,
    // matching the V1 `stream_action: 'replace'` semantic.
    if (typeof parsed.type === 'string') {
      result.type = parsed.type
    }
    // Greptile P2 on PR #14042: `typeof [] === 'object'` is `true`, so
    // a backend version-skew sending `data: [...]` would pass a naive
    // object guard. Explicitly reject arrays (and null) before the
    // per-field shape checks so the loop's `parsed.data.text` access
    // never lands on a non-record value.
    if (
      parsed.data !== null &&
      typeof parsed.data === 'object' &&
      !Array.isArray(parsed.data)
    ) {
      const replaceData: { text?: string; reason?: string } = {}
      if (typeof parsed.data.text === 'string') replaceData.text = parsed.data.text
      if (typeof parsed.data.reason === 'string') replaceData.reason = parsed.data.reason
      if (replaceData.text !== undefined || replaceData.reason !== undefined) {
        result.data = replaceData
      }
    }

    // Handle different possible response formats
    if (typeof parsed === 'string') {
      result.content = parsed
    }

    // Handle case where the entire parsed object is a message object
    // (has role, conversation_id, etc.) - extract content from it
    if (!result.content && parsed.role === 'assistant') {
      // This is a full message object, not a stream chunk
      // Extract text content if available
      if (typeof parsed.content === 'string' && parsed.content) {
        result.content = parsed.content
      }
      // Also extract chart data if present
      if (parsed.charts && !result.charts) {
        result.charts = parsed.charts
      }
      if (parsed.chart_data && !result.chart_data) {
        result.chart_data = parsed.chart_data
      }
      // Also extract sources if present
      if (parsed.sources && !result.sources) {
        result.sources = parsed.sources
      }
    }

    return result
  } catch {
    // If not JSON, treat as raw content
    // But filter out data that looks like raw JSON message objects
    const trimmedData = data.trim()
    if (trimmedData && !trimmedData.startsWith('{') && !trimmedData.startsWith('[')) {
      return { content: trimmedData }
    }
  }

  return {}
}

/**
 * Convert API source dict to SourcePart object for rendering
 */
function convertToSourcePart(source: RawAPISource, index: number): SourcePart {
  return {
    type: 'source' as const,
    sourceId: source.source_id || `source-${index}`,
    citationType: source.type as CitationType,
    confidence: source.confidence,
    content: source.content,
    title: source.title,
    url: source.url,
    // Document fields
    docId: source.doc_id,
    chunkId: source.chunk_id,
    pageNumber: source.page_number,
    // P&ID/Knowledge Graph fields
    entityId: source.entity_id,
    pidSource: source.pid_source,
    relationshipId: source.relationship_id,
    // Timeseries fields
    tagId: source.tag_id,
    timeRangeStart: source.time_range_start,
    timeRangeEnd: source.time_range_end,
  }
}

/**
 * What a send tells the backend about the client the answer lands on.
 *
 * A chart comes back as a standalone document in an iframe, so it cannot read the
 * app's theme and has to be drawn for the right surface. Collected here because
 * every send body has to agree, and inline they did not.
 */
const clientContext = () => ({ color_scheme: currentColorScheme() })

export function useChatV2({
  contextId,
  contextType,
  conversationId: initialConversationId,
  timeseriesOptions,
  onError,
}: UseChatV2Options) {
  const [messages, setMessages] = useState<UIMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [orchestrationStatus, setOrchestrationStatus] = useState<OrchestrationStatusData | null>(null)
  // W16 / TIME-198: timer that briefly holds the `failed` state visible
  // before clearing. Without this, the parent unmounts the line within
  // a tick of the failed frame and the renderer's red-dot styling
  // never actually paints — operators see the line "silently
  // disappear" instead of seeing what failed.
  const failedHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // W16 / TIME-198: ephemeral status line for slow tool calls. Set on
  // `invoked` / `binding_resolved` / `fetching`, cleared on `completed`
  // / `failed` so the UI flashes through the lifecycle without leaving
  // a stale line on screen.
  const [toolProgress, setToolProgress] = useState<ToolProgressData | null>(null)

  // W16 / TIME-198: shared cleanup. Cancels a pending failed-hold
  // timeout AND nulls the visible state. Called from every stream-
  // termination site so a stale line — or a stale failed-hold timer
  // mid-flight when a new turn begins — never outlives the turn.
  const clearToolProgress = useCallback(() => {
    if (failedHoldTimerRef.current) {
      clearTimeout(failedHoldTimerRef.current)
      failedHoldTimerRef.current = null
    }
    setToolProgress(null)
  }, [])

  // Version history: keyed by the preceding user message ID
  const [responseVersions, setResponseVersions] = useState<Map<string, VersionEntry>>(new Map())
  // Keep a ref to latest messages so callbacks don't go stale
  const messagesRef = useRef<UIMessage[]>(messages)
  messagesRef.current = messages

  // Initialize to null so the effect can detect first mount and load messages
  const conversationIdRef = useRef<string | null>(null)
  const lastLoadedConversationRef = useRef<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  // Ref for message loading abort controller (prevents race conditions)
  const messageLoadAbortRef = useRef<AbortController | null>(null)
  // Ref for historian simulation timeout
  const historianSimulationRef = useRef<NodeJS.Timeout | null>(null)
  // What had streamed when processStream threw mid-stream. send()'s catch
  // reads this to decide placeholder cleanup. A dedicated ref (not
  // messagesRef) because the read rejection can land in the same microtask
  // chain as the last content chunk — before React has re-rendered — so
  // message state can lag what actually streamed.
  const streamErrorContentRef = useRef('')

  // Get store state and actions
  const {
    activeAgent,
    setActiveAgent,
    setCurrentConversationId,
    createConversation: createConversationInStore,
    updateConversationFirstMessage,
    updateConversationActiveAgent,
    entireDataCatalog,
  } = useChatV2Store()

  /**
   * Send a message and stream the response
   */
  const sendMessageToBackend = useCallback(
    async (conversationId: string, content: string) => {
      const token = await getAuthToken()

      // Create abort controller for this request
      abortControllerRef.current = new AbortController()

      // Capture start time after token fetch to exclude token refresh latency
      const streamStartTime = Date.now()

      const response = await fetch(
        getUrl(`${CHAT_V2_ENDPOINTS.conversation(conversationId)}/messages`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            // Response-formatting design — declare to the backend that
            // this client can parse Vercel UIMessageStream v1 frames
            // (text-*, tool-*, data-*). The backend's
            // `clientSupportsFormattingV2` gate requires this header in
            // addition to the PostHog `formatting_v2` flag before it
            // emits the v1 wire format. Without this header the
            // backend stays on the legacy SSE shape — which is also
            // what the legacy `apiService.sendMessage` path relies on.
            [VERCEL_AI_STREAM_HEADER]: VERCEL_AI_STREAM_VERSION,
            [INTUIGENCE_PARTS_HEADER]: INTUIGENCE_PARTS_VERSION,
          },
          body: JSON.stringify({
            content,
            // Follow-up question generation is currently synchronous on the backend
            // (it blocks the SSE "done" event). Keep it off to avoid the UI being
            // stuck "loading" after the user-facing answer is complete.
            should_generate_follow_up_questions: false,
            timeseries_options: timeseriesOptions,
            // Chat toggle (fs-516) — the kg-agent reads this to expose the
            // tenant-wide UKG tools + broaden retrieval to the catalog.
            entire_data_catalog: entireDataCatalog,
            ...clientContext(),
          }),
          signal: abortControllerRef.current.signal,
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.statusText}`)
      }

      // Backend's correlation-id middleware always echoes the request
      // correlation id on the response. AGNT-684 binds Langfuse traces
      // to this value, so we stamp it on the assistant message metadata
      // for the kg-trace forensics link (AGNT-685). Null if the header
      // is missing — older backends or non-kg-agent paths.
      const correlationId = response.headers.get('x-correlation-id')

      return { response, streamStartTime, correlationId }
    },
    [timeseriesOptions, entireDataCatalog]
  )

  /**
   * Simulate historian progress with timed steps
   */
  const simulateHistorianProgress = useCallback(() => {
    // Clear any existing simulation
    if (historianSimulationRef.current) {
      clearTimeout(historianSimulationRef.current)
    }

    const steps: { status: OrchestrationStatusType; description: string; delay: number }[] = [
      { status: 'analyzing', description: 'Historian analyzing your query...', delay: 1500 },
      { status: 'retrieving', description: 'Retrieving operational data...', delay: 2500 },
      { status: 'generating', description: 'Preparing your answer...', delay: 0 }, // Stays until response
    ]

    let stepIndex = 0
    const showNextStep = () => {
      if (stepIndex < steps.length) {
        setOrchestrationStatus({
          status: steps[stepIndex].status,
          description: steps[stepIndex].description,
        })
        if (steps[stepIndex].delay > 0) {
          historianSimulationRef.current = setTimeout(showNextStep, steps[stepIndex].delay)
        }
        stepIndex++
      }
    }
    showNextStep()
  }, [])

  /**
   * Process SSE stream and update messages
   */
  const processStream = useCallback(
    async (response: Response, assistantMessageId: string, conversationId: string, streamStartTime: number) => {
      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body')
      }

      const decoder = new TextDecoder()
      let fullContent = ''
      let fullThinking = ''
      let streamComplete = false
      let buffer = '' // Buffer for incomplete SSE lines across HTTP chunks
      let backendAgentId: AgentId | null = null

      // PR 16 follow-up: dispatch on the Vercel UIMessageStream protocol
      // header. When formatting_v2 is on, the backend emits
      // `text-*`/`tool-*`/`data-*` frames per Vercel's v1 wire format
      // instead of the legacy `{content, thinking, chart_data, ...}` SSE
      // shape. The legacy parser below is unchanged; the v1 branch
      // accumulates frames via the same pure functions useChatV3 uses
      // (`parseChunk` + `reduceFramesToParts`) and writes the resulting
      // parts onto the existing assistant message. Field aliasing on
      // tool parts (input→args, output→result) lets the legacy
      // AssistantMessage renderer continue to work for pure-text +
      // tool messages; data-* parts route to RenderedAssistantMessage
      // via AssistantMessageRouter.
      const isV2Protocol =
        response.headers.get(VERCEL_AI_STREAM_HEADER) === VERCEL_AI_STREAM_VERSION
      const v2Frames: Parameters<typeof reduceFramesToParts>[0] = []

      try {
        if (isV2Protocol) {
          while (!streamComplete) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const { frames, done: hadDone, remainder } = parseChunk(buffer)
            buffer = remainder
            if (frames.length > 0) {
              v2Frames.push(...frames)
              // buildV2StreamParts reduces the frames to legacy parts and
              // folds in the kg-agent `data-kg-sources` snapshot as
              // `source` parts, so the citation footer renders live.
              const partsLegacy = buildV2StreamParts(v2Frames)
              const textPart = partsLegacy.find(
                (p): p is { type: 'text'; text: string } => p.type === 'text',
              )
              const nextFullContent = textPart?.text ?? ''
              if (nextFullContent.trim() && !fullContent.trim()) {
                if (historianSimulationRef.current) {
                  clearTimeout(historianSimulationRef.current)
                  historianSimulationRef.current = null
                }
                setOrchestrationStatus(null)
              }
              fullContent = nextFullContent
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, parts: partsLegacy, status: 'streaming' }
                    : msg,
                ),
              )
            }
            if (hadDone) {
              streamComplete = true
              break
            }
          }
        } else {
        while (!streamComplete) {
          const { done, value } = await reader.read()
          if (done) break

          // Accumulate chunks into buffer to handle large responses split across HTTP chunks
          buffer += decoder.decode(value, { stream: true })

          // Split on newlines, but keep the last element (may be incomplete) in buffer
          const lines = buffer.split('\n')
          buffer = lines.pop() || '' // Save incomplete line for next iteration

          for (const line of lines) {
            // Handle SSE event type 'done' (backend sends: event: done)
            if (line.startsWith('event: done') || line === 'event:done') {
              // Stream is complete
              streamComplete = true
              break
            }

            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              const parsed = parseSSEData(data)

              if (parsed.done) {
                streamComplete = true
                break
              }

              if (parsed.error) {
                throw new Error(parsed.error)
              }

              // Skip system messages — they are internal and should never be rendered
              if (parsed.role === 'system') {
                continue
              }

              // Backend-selected agent: keep UI (avatar/header) in sync without refresh
              if (parsed.agent_used) {
                const mapped = mapBackendAgentUsedToAgentId(parsed.agent_used)
                if (mapped && mapped !== backendAgentId) {
                  backendAgentId = mapped

                  // Update assistant message avatar/badge
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? {
                            ...msg,
                            metadata: {
                              ...msg.metadata,
                              agentUsed: mapped,
                            },
                          }
                        : msg
                    )
                  )

                  // Update conversation + global active agent (header / follow-up routing)
                  updateConversationActiveAgent(conversationId, mapped)
                  setActiveAgent(mapped)
                }
              }

              // Handle user message ID sync event (backend sends actual DB ID as first event)
              // This allows editing messages immediately after sending
              if (parsed.role === 'user' && parsed.id && !parsed.content) {
                const syncedUserId = parsed.id
                setMessages((prev) => {
                  // Find the last user message and update its ID
                  for (let i = prev.length - 1; i >= 0; i--) {
                    if (prev[i].role === 'user') {
                      return prev.map((msg, index) =>
                        index === i ? { ...msg, id: syncedUserId } : msg
                      )
                    }
                  }
                  return prev
                })
                continue // Don't process further - this was just an ID sync
              }

              // W16 / TIME-198: surface or clear the tool-progress
              // status line. Pre-terminal states keep it pinned with
              // the latest detail. Terminal states are split:
              //   - `completed`: clear immediately (the answer text
              //     about to stream is the success signal).
              //   - `failed`: render the failed state for ~800ms
              //     before clearing, so the renderer's red-dot
              //     styling actually paints. Clearing within a tick
              //     left operators staring at a silent disappearance
              //     — Abdullah and Greptile both flagged this on
              //     PRs #10353/#10354/#10355.
              if (parsed.toolProgress) {
                if (failedHoldTimerRef.current) {
                  clearTimeout(failedHoldTimerRef.current)
                  failedHoldTimerRef.current = null
                }
                if (parsed.toolProgress.status === 'completed') {
                  setToolProgress(null)
                } else if (parsed.toolProgress.status === 'failed') {
                  setToolProgress(parsed.toolProgress)
                  failedHoldTimerRef.current = setTimeout(() => {
                    setToolProgress(null)
                    failedHoldTimerRef.current = null
                  }, FAILED_HOLD_MS)
                } else {
                  setToolProgress(parsed.toolProgress)
                }
                continue
              }

              if (parsed.workflowProgress) {
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? {
                          ...msg,
                          parts: upsertWorkflowProgressPart(msg.parts, parsed.workflowProgress!),
                          status: 'streaming',
                        }
                      : msg
                  )
                )
                continue
              }

              // FS-676: egress-consent prompt — attach the approve/deny card
              // to this message. Arrives at most once per turn, at turn end.
              if (parsed.consentRequest) {
                const surfaces = parsed.consentRequest.surfaces
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? {
                          ...msg,
                          metadata: {
                            ...msg.metadata,
                            consentRequest: { surfaces, status: 'pending' as const },
                          },
                        }
                      : msg
                  )
                )
                continue
              }

              // Handle follow-up questions
              if (parsed.follow_up_questions) {
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? {
                          ...msg,
                          metadata: {
                            ...msg.metadata,
                            followUpQuestions: parsed.follow_up_questions,
                          },
                        }
                      : msg
                  )
                )
              }

              // Handle chart data from historian backend
              if (parsed.chart_data && Array.isArray(parsed.chart_data) && parsed.chart_data.length > 0) {
                const fileParts: FilePart[] = parsed.chart_data.map((chart: ChartData, index: number) => ({
                  type: 'file' as const,
                  url: chart.url || '',
                  mediaType: chart.type || 'image/png',
                  title: chart.title || chart.filename || `Chart ${index + 1}`,
                  data: chart.data,
                }))

                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? {
                          ...msg,
                          parts: [...msg.parts.filter((p) => p.type !== 'file'), ...fileParts],
                          metadata: {
                            ...msg.metadata,
                            chartData: parsed.chart_data,
                          },
                        }
                      : msg
                  )
                )
              }

              // Handle sources from grounding validation
              if (parsed.sources && Array.isArray(parsed.sources) && parsed.sources.length > 0) {
                const sourceParts: SourcePart[] = parsed.sources.map(convertToSourcePart)

                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? {
                          ...msg,
                          parts: [...msg.parts.filter((p) => p.type !== 'source'), ...sourceParts],
                        }
                      : msg
                  )
                )
              }

              // Accumulate thinking from Claude Direct Mode
              if (typeof parsed.thinking === 'string') {
                fullThinking += parsed.thinking
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? {
                          ...msg,
                          parts: [
                            ...(fullThinking ? [{ type: 'reasoning' as const, text: fullThinking }] : []),
                            ...msg.parts.filter((p) => p.type === 'text'),
                            ...preserveNonTextParts(msg.parts),
                          ],
                        }
                      : msg
                  )
                )
              }

              // ENG-2419 PR B — V2 SSE replace frame (data-text-replace).
              // V2 UI-message-stream emits `{ type: 'data-text-replace',
              // data: { text, reason } }` from the backend's
              // `bundle.replaceText(finalText)` call when groundedness regen
              // forces a stock abstain (PR #13990 + AGNT-767). The V1 SSE
              // path below (`stream_action: 'replace'`) already handled the
              // legacy chat flow, but V2 traffic was silently dropping the
              // abstain — the user kept seeing the hallucinated draft even
              // though `[KG_AGENT_ZERO_TOOL_GUARDRAIL_ABSTAIN]` fired
              // server-side. Mirror the V1 replace semantic: rewrite
              // `fullContent`, then push a fresh text part.
              if (
                parsed.type === 'data-text-replace' &&
                parsed.data &&
                typeof parsed.data.text === 'string'
              ) {
                fullContent = parsed.data.text
                // Self-review nit #1 on PR #14042: clear thinking on
                // replace. The streamed thinking rationalized the
                // hallucinated draft that's now being replaced — showing
                // it next to the abstain reads "thinking: the answer is
                // R279032011P01 because... / text: I don't have a
                // reliable answer". V1's ChatInterface effectively does
                // the same via the envelope's hard-empty `thinking: ''`
                // overriding `msg.thinking` on the isReplace branch
                // (index.tsx:802-805). Match that here so V1 and V2 wire
                // paths render the abstain identically. `reasoningPath`
                // metadata is already cleared below.
                fullThinking = ''
                if (historianSimulationRef.current) {
                  clearTimeout(historianSimulationRef.current)
                  historianSimulationRef.current = null
                }
                setOrchestrationStatus(null)
                const sanitizedReplaceContent = stripInternalMetadata(fullContent)
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? {
                          ...msg,
                          parts: [
                            // Self-review nit #2 on PR #14042: keep
                            // sources/files (parallel to V1, which uses
                            // `newMessage.charts || msg.charts` on the
                            // isReplace branch — no strip). The abstain
                            // text may still reference the same
                            // upstream tool outputs; the regen pass
                            // doesn't invalidate them. If a future
                            // groundedness scan should drop sources
                            // tied to a hallucinated draft, that's a
                            // backend regen-pass concern (drop the
                            // source from the bundled tool outputs
                            // before computing the abstain), not a
                            // frontend strip.
                            { type: 'text', text: sanitizedReplaceContent },
                            ...preserveNonTextParts(msg.parts),
                          ],
                          status: 'streaming',
                          metadata: {
                            ...msg.metadata,
                            reasoningPath: undefined,
                          },
                        }
                      : msg
                  )
                )
                continue
              }

              if (typeof parsed.content === 'string') {
                const chunk = parsed.content
                const action = parsed.stream_action === 'replace' ? 'replace' : 'append'

                // Skip content that looks like raw JSON (backend error)
                if (chunk.trim().startsWith('{') || chunk.trim().startsWith('[')) {
                  continue
                }

                if (action === 'replace') {
                  fullContent = chunk
                } else {
                  fullContent += chunk
                }

                // Clear orchestration status and historian simulation when actual content starts arriving
                if (fullContent.trim()) {
                  if (historianSimulationRef.current) {
                    clearTimeout(historianSimulationRef.current)
                    historianSimulationRef.current = null
                  }
                  setOrchestrationStatus(null)
                }

                // Update the assistant message with accumulated content
                // AGNT-335: Strip any internal tool metadata before rendering
                const sanitizedContent = stripInternalMetadata(fullContent)
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? {
                          ...msg,
                          parts: [
                            ...(fullThinking ? [{ type: 'reasoning' as const, text: fullThinking }] : []),
                            { type: 'text', text: sanitizedContent },
                            // Preserve any file parts (charts) and source parts
                            ...preserveNonTextParts(msg.parts),
                          ],
                          status: 'streaming',
                          metadata: {
                            ...msg.metadata,
                            // Do not persist internal reasoning paths in metadata.
                            reasoningPath: undefined,
                          },
                        }
                      : msg
                  )
                )
              }
            }
          }
        }

        // Process any remaining buffer content after stream ends
        // (handles case where final chunk didn't end with newline)
        if (buffer.trim() && buffer.startsWith('data: ')) {
          const data = buffer.slice(6)
          const parsed = parseSSEData(data)

          if (parsed.workflowProgress) {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessageId
                  ? {
                      ...msg,
                      parts: upsertWorkflowProgressPart(msg.parts, parsed.workflowProgress!),
                      status: 'streaming',
                    }
                  : msg
              )
            )
          }

          // Handle chart data from final buffer
          if (parsed.chart_data && Array.isArray(parsed.chart_data) && parsed.chart_data.length > 0) {
            const fileParts: FilePart[] = parsed.chart_data.map((chart: ChartData, index: number) => ({
              type: 'file' as const,
              url: chart.url || '',
              mediaType: chart.type || 'image/png',
              title: chart.title || chart.filename || `Chart ${index + 1}`,
              data: chart.data,
            }))

            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessageId
                  ? {
                      ...msg,
                      parts: [...msg.parts.filter((p) => p.type !== 'file'), ...fileParts],
                      metadata: {
                        ...msg.metadata,
                        chartData: parsed.chart_data,
                      },
                    }
                  : msg
              )
            )
          }

          // Handle sources from final buffer
          if (parsed.sources && Array.isArray(parsed.sources) && parsed.sources.length > 0) {
            const sourceParts: SourcePart[] = parsed.sources.map(convertToSourcePart)

            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessageId
                  ? {
                      ...msg,
                      parts: [...msg.parts.filter((p) => p.type !== 'source'), ...sourceParts],
                    }
                  : msg
              )
            )
          }

          // Handle data-text-replace from final buffer (greptile P1 on
          // PR #14042). `bundle.replaceText(finalText)` fires post-stream
          // — exactly the frame class most at risk of landing in the
          // buffer when the connection closes after a single trailing
          // `\n` rather than `\n\n`. Without this branch the abstain
          // would be silently dropped on a subset of connections,
          // reproducing the original ENG-2419 symptom even with the
          // in-loop fix in place. Mirrors the in-loop branch byte-for-
          // byte: swap `fullContent`, clear `fullThinking`, push a
          // fresh text part.
          if (
            parsed.type === 'data-text-replace' &&
            parsed.data &&
            typeof parsed.data.text === 'string'
          ) {
            fullContent = parsed.data.text
            fullThinking = ''
            if (historianSimulationRef.current) {
              clearTimeout(historianSimulationRef.current)
              historianSimulationRef.current = null
            }
            setOrchestrationStatus(null)
            const sanitizedReplaceBuffer = stripInternalMetadata(fullContent)
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessageId
                  ? {
                      ...msg,
                      parts: [
                        { type: 'text', text: sanitizedReplaceBuffer },
                        ...preserveNonTextParts(msg.parts),
                      ],
                      status: 'streaming',
                      metadata: {
                        ...msg.metadata,
                        reasoningPath: undefined,
                      },
                    }
                  : msg
              )
            )
          } else if (typeof parsed.content === 'string') {
            // Handle content from final buffer
            const chunk = parsed.content
            const action = parsed.stream_action === 'replace' ? 'replace' : 'append'
            if (!chunk.trim().startsWith('{') && !chunk.trim().startsWith('[')) {
              if (action === 'replace') {
                fullContent = chunk
              } else {
                fullContent += chunk
              }

              if (historianSimulationRef.current) {
                clearTimeout(historianSimulationRef.current)
                historianSimulationRef.current = null
              }
              setOrchestrationStatus(null)

              // AGNT-335: Strip any internal tool metadata before rendering
              const sanitizedBufferContent = stripInternalMetadata(fullContent)
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? {
                        ...msg,
                        parts: [
                          { type: 'text', text: sanitizedBufferContent },
                          ...preserveNonTextParts(msg.parts),
                        ],
                        status: 'streaming',
                        metadata: {
                          ...msg.metadata,
                          reasoningPath: undefined,
                        },
                      }
                    : msg
                )
              )
            }
          }
        }
        }
      } catch (streamErr) {
        // A mid-stream throw bypasses the clean-end handling below. Record
        // what had streamed so the caller's catch can decide placeholder
        // cleanup synchronously (state/render may not have caught up yet).
        streamErrorContentRef.current = fullContent
        throw streamErr
      } finally {
        reader.releaseLock()
      }

      // Mark message as ready and clear orchestration status
      if (historianSimulationRef.current) {
        clearTimeout(historianSimulationRef.current)
        historianSimulationRef.current = null
      }
      setOrchestrationStatus(null)
      // Defensive clear: if the stream ended without a `completed`/
      // `failed` tool_progress frame (e.g. backend missed it), drop
      // any stale status line — and cancel any pending failed-hold
      // timer — so they don't outlive the answer.
      clearToolProgress()
      const responseDurationMs = Date.now() - streamStartTime
      if (fullContent.trim().length === 0) {
        // Stream ended with no visible content (e.g. upstream rate-limit error
        // that returned 200 but no text frames). Remove the placeholder so it
        // doesn't accumulate in the message list across failed attempts.
        setMessages((prev) => prev.filter((msg) => msg.id !== assistantMessageId))
      } else {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId ? { ...msg, status: 'ready', createdAt: new Date(), metadata: { ...msg.metadata, responseDurationMs } } : msg
          )
        )
      }

      // AGNT-335: Return sanitized content so callers never see raw tool metadata
      return stripInternalMetadata(fullContent)
    },
    [simulateHistorianProgress, setActiveAgent, updateConversationActiveAgent]
  )

  /**
   * Send a message
   */
  const send = useCallback(
    async (content: string, files?: File[]) => {
      if (!content.trim() && (!files || files.length === 0)) return

      setError(null)
      setIsLoading(true)

      // Set initial queuing status while waiting for backend
      setOrchestrationStatus({
        status: 'queuing',
        description: 'Sending your request…',
      })

      // Hoisted so the catch can clean up the streaming placeholder if the
      // stream throws before processStream's own clean-end handling runs.
      let createdAssistantId: string | undefined
      // A throw before processStream (e.g. the POST itself failed) must read
      // as "nothing streamed", not a stale value from a previous turn.
      streamErrorContentRef.current = ''

      try {
        // Build message content with file info first
        let messageContent = content.trim()
        if (files && files.length > 0) {
          const fileNames = files.map((f) => f.name).join(', ')
          messageContent += `\n\n[Attached files: ${fileNames}]`
        }

        // AGENT SWITCHING LOGIC (matches old chat behavior)

        // Keep original content for UI display
        const displayContent = messageContent

        // Convert short mentions (@max, @aice) to long format (@ai-mechanical-engineer)
        // for backend compatibility, while preserving user's choice in UI
        const backendContent = messageContent.replace(
          /@(pam|aice|max|ella)\b/gi,
          (_match, agentId) => {
            const agentIdLower = agentId.toLowerCase() as AgentId
            return getAgentMentionName(agentIdLower)
          }
        )

        // Track the agent we intend to use for this request (may be overridden by backend routing)
        let requestAgent: AgentId = activeAgent

        // 1. Check for multiple agent mentions
        if (hasMultipleAgentMentions(backendContent)) {
          // Multiple agents mentioned → route through plant_support_engineer
          requestAgent = 'pam'
        } else {
          // 2. Detect single @mention and switch agent
          const mentionedAgent = detectAgentMention(backendContent)
          if (mentionedAgent) {
            requestAgent = mentionedAgent
          }
        }

        // Apply request agent selection to store (for header + orchestration UI)
        if (requestAgent !== activeAgent) {
          setActiveAgent(requestAgent)
        }

        // Use backendContent for sending to backend
        messageContent = backendContent

        // Track if this is a new conversation
        const isNewConversation = !conversationIdRef.current

        // Create conversation if needed (using store action)
        let convId = conversationIdRef.current
        if (!convId) {
          convId = await createConversationInStore(contextId, contextType)
          conversationIdRef.current = convId
          lastLoadedConversationRef.current = convId // Mark as loaded to prevent reload
          setCurrentConversationId(convId) // Set after refs to avoid race condition
        }

        // Add user message to state
        // Use displayContent (without auto-prepended @mention) for UI, but send messageContent to backend
        const userMessage: UIMessage = {
          id: uuidv4(),
          role: 'user',
          parts: [{ type: 'text', text: displayContent }],
          createdAt: new Date(),
          status: 'ready',
        }
        setMessages((prev) => [...prev, userMessage])

        // Add placeholder assistant message
        // Note: activeAgent has already been set based on user's @mention above,
        // ensuring user's agent preference takes priority over any backend defaults
        const assistantMessageId = uuidv4()
        createdAssistantId = assistantMessageId
        const assistantMessage: UIMessage = {
          id: assistantMessageId,
          role: 'assistant',
          parts: [{ type: 'text', text: '' }],
          createdAt: new Date(),
          status: 'streaming',
          metadata: { agentUsed: requestAgent },
        }
        setMessages((prev) => [...prev, assistantMessage])

        // Send message and process stream
        const { response, streamStartTime, correlationId } = await sendMessageToBackend(convId, messageContent)
        if (correlationId) {
          // Stamp the correlation id on the placeholder assistant
          // message before streaming begins so the forensics link is
          // available as soon as the bubble renders. Stream chunks
          // merge into the existing message; the metadata stays.
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId
                ? { ...m, metadata: { ...m.metadata, correlationId } }
                : m
            )
          )
        }
        await processStream(response, assistantMessageId, convId, streamStartTime)

        // Update firstMessage for new conversations
        // Use displayContent (without auto-prepended @mention) for conversation title
        if (isNewConversation && convId) {
          updateConversationFirstMessage(convId, displayContent)
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error')
        if (error.name !== 'AbortError') {
          // Clear historian simulation timer on error
          if (historianSimulationRef.current) {
            clearTimeout(historianSimulationRef.current)
            historianSimulationRef.current = null
          }
          setOrchestrationStatus(null)
          clearToolProgress()
          setError(error)
          onError?.(error)
          toast.error('Failed to send message')
          console.error('Chat error:', error)

          // A mid-stream throw (e.g. a network drop reading the SSE body on a
          // long/slow response) bypasses processStream's clean-end handling,
          // leaving the empty 'streaming' placeholder stuck as a blank bubble.
          // Mirror the clean-end behavior: drop it if nothing rendered, else
          // stop the streaming state (mark errored) so partial content still
          // shows.
          if (createdAssistantId) {
            const placeholderId = createdAssistantId
            // hadContent comes from streamErrorContentRef (written by
            // processStream at the throw site), not from message state — the
            // rejection can outrun the render of the last chunk.
            const hadContent = streamErrorContentRef.current.trim().length > 0
            if (!hadContent) {
              setMessages((prev) => prev.filter((m) => m.id !== placeholderId))
            } else {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === placeholderId ? { ...m, status: 'error' as const } : m
                )
              )
            }
          }
        }
      } finally {
        setIsLoading(false)
        abortControllerRef.current = null
      }
    },
    [
      activeAgent,
      contextId,
      contextType,
      createConversationInStore,
      onError,
      processStream,
      sendMessageToBackend,
      setActiveAgent,
      setCurrentConversationId,
      updateConversationFirstMessage,
    ]
  )

  /**
   * Stop the current response
   */
  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsLoading(false)
    // Drop any in-flight tool-progress status line — the abort means
    // we'll never see the terminal `completed`/`failed` frame.
    clearToolProgress()

    // Mark any streaming messages as ready
    setMessages((prev) =>
      prev.map((msg) =>
        msg.status === 'streaming' ? { ...msg, status: 'ready' } : msg
      )
    )
  }, [])

  /**
   * Reload the last response
   */
  const reload = useCallback(async () => {
    const msgs = messagesRef.current
    // Find the last user message
    const lastUserMessageIndex = msgs.findLastIndex((m) => m.role === 'user')
    if (lastUserMessageIndex === -1) return

    const lastUserMessage = msgs[lastUserMessageIndex]
    const userMsgId = lastUserMessage.id
    const content = lastUserMessage.parts
      .filter((p) => p.type === 'text')
      .map((p) => p.text || '')
      .join('\n\n')

    // Archive the current assistant response before removing it
    // Skip archiving failed/empty responses — no point versioning a blank message
    const currentAssistantMsg = msgs[lastUserMessageIndex + 1]
    const hasContent = currentAssistantMsg?.parts?.some(
      (p) =>
        (p.type === 'text' && p.text && p.text.trim().length > 0) ||
        p.type === 'file' ||
        p.type === 'source'
    )
    if (currentAssistantMsg && currentAssistantMsg.role === 'assistant' && hasContent) {
      setResponseVersions((prev) => {
        const next = new Map(prev)
        const existing = next.get(userMsgId)
        const entry = existing
          ? { ...existing, responses: [...existing.responses] }
          : { responses: [], currentIndex: 0, liveMessage: null }

        // If user is viewing an old version, push liveMessage back to responses
        if (entry.liveMessage) {
          // Dedup check
          if (!entry.responses.some((r) => r.id === entry.liveMessage!.id)) {
            entry.responses.push(entry.liveMessage)
          }
          entry.liveMessage = null
        }

        // Archive the current displayed message (dedup check)
        if (!entry.responses.some((r) => r.id === currentAssistantMsg.id)) {
          entry.responses.push(currentAssistantMsg)
        }

        // Reset to live (new response will be at index = responses.length)
        entry.currentIndex = entry.responses.length
        next.set(userMsgId, entry)
        return next
      })
    }

    // Remove messages after the last user message
    setMessages((prev) => prev.slice(0, lastUserMessageIndex + 1))

    // Re-send with regenerate flag
    const convId = conversationIdRef.current
    if (!convId) return

    setIsLoading(true)
    setError(null)

    try {
      const token = await getAuthToken()
      abortControllerRef.current = new AbortController()

      const streamStartTime = Date.now()
      const response = await fetch(
        getUrl(`${CHAT_V2_ENDPOINTS.conversation(convId)}/messages`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            content,
            regenerate: true,
            timeseries_options: timeseriesOptions,
            // Chat toggle (fs-516) — respect current toggle state on regen.
            entire_data_catalog: entireDataCatalog,
            ...clientContext(),
          }),
          signal: abortControllerRef.current.signal,
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to regenerate: ${response.statusText}`)
      }

      const assistantMessageId = uuidv4()
      const assistantMessage: UIMessage = {
        id: assistantMessageId,
        role: 'assistant',
        parts: [{ type: 'text', text: '' }],
        createdAt: new Date(),
        status: 'streaming',
        metadata: { agentUsed: activeAgent },
      }
      setMessages((prev) => [...prev, assistantMessage])

      await processStream(response, assistantMessageId, convId, streamStartTime)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error')
      if (error.name !== 'AbortError') {
        // Clear historian simulation timer on error
        if (historianSimulationRef.current) {
          clearTimeout(historianSimulationRef.current)
          historianSimulationRef.current = null
        }
        setOrchestrationStatus(null)
        clearToolProgress()
        setError(error)
        onError?.(error)
        toast.error('Failed to regenerate response')
      }
    } finally {
      setIsLoading(false)
      abortControllerRef.current = null
    }
  }, [activeAgent, onError, processStream, timeseriesOptions, entireDataCatalog])

  /**
   * FS-676: answer an egress-consent prompt (the approve/deny card a
   * `consent_request` frame attached to an assistant message). Records the
   * verdict server-side (agent_egress_consent — what actually arms later
   * turns), then on approval re-runs the question the offer was made for via
   * the regenerate flow, which now streams the web-armed answer in place.
   */
  const respondToConsent = useCallback(
    async (messageId: string, verdict: 'granted' | 'denied') => {
      const convId = conversationIdRef.current
      if (!convId) return
      const setCardStatus = (status: 'pending' | 'granted' | 'denied') =>
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId && msg.metadata?.consentRequest
              ? {
                  ...msg,
                  metadata: {
                    ...msg.metadata,
                    consentRequest: { ...msg.metadata.consentRequest!, status },
                  },
                }
              : msg
          )
        )
      // Optimistically resolve the card so a double-click can't double-fire.
      setCardStatus(verdict)
      try {
        await apiService.updateEgressConsent(convId, { surface: 'web_search', verdict })
      } catch {
        // Revert so the buttons come back and the user can retry.
        setCardStatus('pending')
        toast.error('Failed to record your choice — please try again')
        return
      }
      if (verdict !== 'granted') return
      // Re-run the question the offer was made for. `reload()` regenerates
      // the LAST turn only; when the card sits on an older message the grant
      // still sticks (it arms every later turn in this conversation), so
      // just confirm instead of regenerating an unrelated question.
      const msgs = messagesRef.current
      const lastAssistant = [...msgs].reverse().find((m) => m.role === 'assistant')
      if (lastAssistant?.id === messageId) {
        await reload()
      } else {
        toast.success('Web search enabled for this conversation')
      }
    },
    [reload]
  )

  /**
   * Reset chat (new conversation)
   */
  const reset = useCallback(() => {
    stop()
    conversationIdRef.current = null
    setCurrentConversationId(null)
    setMessages([])
    setInput('')
    setError(null)
    setResponseVersions(new Map())
  }, [stop, setCurrentConversationId])

  /**
   * Edit a message and regenerate response
   * @param onEditSaved - Optional callback called after edit is saved but before streaming starts
   */
  const editMessage = useCallback(
    async (messageId: string, newContent: string, onEditSaved?: () => void) => {
      const currentMessages = messagesRef.current
      const messageIndex = currentMessages.findIndex((m) => m.id === messageId)
      if (messageIndex === -1) {
        console.error('Message not found for editing:', messageId)
        return
      }

      const messageToEdit = currentMessages[messageIndex]
      if (messageToEdit.role !== 'user') {
        console.error('Can only edit user messages')
        return
      }

      const convId = conversationIdRef.current
      if (!convId) return

      setIsLoading(true)
      setOrchestrationStatus({ status: 'queuing', description: 'Updating your message…' })

      try {
        const token = await getAuthToken()

        // STEP 1: Native edit archives the old turn and later messages atomically.
        // The shared API helper also supplies Fabric embed authentication headers.
        await apiService.editMessage(convId, messageId, newContent)

        // Archive the assistant response that follows the edited message (if any)
        // Skip archiving failed/empty responses — no point versioning a blank message
        const currentMsgs = messagesRef.current
        const assistantAfter = currentMsgs[messageIndex + 1]
        const hasEditContent = assistantAfter?.parts?.some(
          (p) =>
            (p.type === 'text' && p.text && p.text.trim().length > 0) ||
            p.type === 'file' ||
            p.type === 'source'
        )
        if (assistantAfter && assistantAfter.role === 'assistant' && hasEditContent) {
          setResponseVersions((prev) => {
            const next = new Map(prev)
            const existing = next.get(messageId)
            const entry = existing
              ? { ...existing, responses: [...existing.responses] }
              : { responses: [], currentIndex: 0, liveMessage: null }

            // If viewing an old version, push liveMessage back
            if (entry.liveMessage) {
              if (!entry.responses.some((r) => r.id === entry.liveMessage!.id)) {
                entry.responses.push(entry.liveMessage)
              }
              entry.liveMessage = null
            }

            // Archive the current displayed assistant message
            if (!entry.responses.some((r) => r.id === assistantAfter.id)) {
              entry.responses.push(assistantAfter)
            }

            entry.currentIndex = entry.responses.length
            next.set(messageId, entry)
            return next
          })
        }

        // Clean up version entries for any user messages that will be deleted downstream
        const deletedUserMsgIds = currentMsgs
          .slice(messageIndex + 1)
          .filter((m) => m.role === 'user')
          .map((m) => m.id)
        if (deletedUserMsgIds.length > 0) {
          setResponseVersions((prev) => {
            const next = new Map(prev)
            for (const id of deletedUserMsgIds) {
              next.delete(id)
            }
            return next
          })
        }

        // STEP 2: Update local state (truncate messages after edited one)
        setMessages((prev) => {
          const updatedMessages = prev.slice(0, messageIndex)
          updatedMessages.push({
            ...messageToEdit,
            parts: [{ type: 'text', text: newContent }],
          })
          return updatedMessages
        })

        // AGENT SWITCHING LOGIC (same as send function)
        if (hasMultipleAgentMentions(newContent)) {
          setActiveAgent('pam')
        } else {
          const mentionedAgent = detectAgentMention(newContent)
          if (mentionedAgent) {
            setActiveAgent(mentionedAgent)
          }
        }

        // STEP 3: Send regenerate request to get new AI response
        abortControllerRef.current = new AbortController()

        const streamStartTime = Date.now()
        const response = await fetch(
          getUrl(`${CHAT_V2_ENDPOINTS.conversation(convId)}/messages`),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              content: newContent,
              regenerate: true,
              should_generate_follow_up_questions: true, // Enable follow-up questions for edited messages
              timeseries_options: timeseriesOptions,
              // Chat toggle (fs-516) — respect current toggle state on edit.
              entire_data_catalog: entireDataCatalog,
              ...clientContext(),
            }),
            signal: abortControllerRef.current.signal,
          }
        )

        if (!response.ok) {
          throw new Error(`Failed to send edited message: ${response.statusText}`)
        }

        const assistantMessageId = uuidv4()
        const assistantMessage: UIMessage = {
          id: assistantMessageId,
          role: 'assistant',
          parts: [{ type: 'text', text: '' }],
          createdAt: new Date(),
          status: 'streaming',
          metadata: { agentUsed: activeAgent },
        }
        setMessages((prev) => [...prev, assistantMessage])

        // Call callback to notify edit is saved (edit button can stop loading)
        onEditSaved?.()

        // Process stream (blocking - keeps isLoading true during streaming)
        // This is important for streaming UI to work correctly (isStreaming calculation)
        await processStream(response, assistantMessageId, convId, streamStartTime)
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error')
        if (error.name !== 'AbortError') {
          // Clear historian simulation timer on error
          if (historianSimulationRef.current) {
            clearTimeout(historianSimulationRef.current)
            historianSimulationRef.current = null
          }
          setOrchestrationStatus(null)
          clearToolProgress()
          setError(error)
          onError?.(error)
          toast.error('Failed to edit message')
        }
        // Always call onEditSaved to clear loading state in MessageList
        onEditSaved?.()
      } finally {
        setIsLoading(false)
        setOrchestrationStatus(null)
        clearToolProgress()
        abortControllerRef.current = null
      }
    },
    [activeAgent, onError, processStream, setActiveAgent, timeseriesOptions, entireDataCatalog]
  )

  /**
   * Load messages for a conversation
   */
  const loadConversationMessages = useCallback(
    async (convId: string) => {
      // Abort any previous message load to prevent race conditions
      if (messageLoadAbortRef.current) {
        messageLoadAbortRef.current.abort()
      }
      messageLoadAbortRef.current = new AbortController()

      setIsLoadingMessages(true)
      try {
        const response = await apiService.fetchOrError(
          getUrl(`/v1/conversations/${convId}/messages`),
          { signal: messageLoadAbortRef.current.signal }
        )

        if (!response.ok) {
          throw new Error(`Failed to load messages: ${response.statusText}`)
        }

        const data = await response.json()

        // Check if user switched to a different conversation while loading
        // If so, discard these messages to prevent race condition
        if (conversationIdRef.current !== convId) {
          return
        }

        const loadedMessages: UIMessage[] = (data.messages || data || [])
          .filter((msg: RawAPIMessage) => msg.role !== 'system')
          .map((msg: RawAPIMessage) => {
            const agentUsed = mapBackendAgentUsedToAgentId(msg.agent_used)

            // Convert chart_data to FilePart objects
            const fileParts: FilePart[] = (msg.chart_data || []).map(
              (chart: ChartData, index: number) => ({
                type: 'file' as const,
                url: chart.url || '',
                mediaType: chart.type || 'image/png',
                title: chart.title || chart.filename || `Chart ${index + 1}`,
                data: chart.data,
              })
            )

            // Convert sources to SourcePart objects
            const sourceParts: SourcePart[] = (msg.sources || []).map(convertToSourcePart)

            // Build parts array with text content, charts, and sources
            // AGNT-335: Sanitize historical messages that were stored before
            // the backend started stripping internal metadata at write time.
            // AGNT-556: Prepend the persisted reasoning trace as a
            // ReasoningPart so the collapsed "Thinking process" pane
            // survives navigation. Mirror the streaming order (reasoning
            // before text) so the UI looks identical pre/post reload.
            const reasoningParts: MessagePart[] =
              typeof msg.thinking === 'string' && msg.thinking.length > 0
                ? [{ type: 'reasoning', text: msg.thinking }]
                : []
            const persistedParts =
              Array.isArray(msg.parts) && msg.parts.length > 0
                ? mapV3PartsToLegacyParts(msg.parts).map((part) =>
                    part.type === 'text'
                      ? { ...part, text: stripInternalMetadata(part.text) }
                      : part
                  )
                : undefined
            const parts: MessagePart[] =
              persistedParts ??
              [
                ...reasoningParts,
                { type: 'text', text: stripInternalMetadata(msg.content ?? '') },
                ...fileParts,
                ...sourceParts,
              ]

            return {
              id: msg.id,
              role: msg.role as MessageRole,
              parts,
              createdAt: msg.timestamp ? new Date(msg.timestamp) : new Date(),
              status: 'ready',
              metadata: {
                agentUsed: agentUsed || undefined,
                feedback: msg.feedback_value ?? undefined,
                reasoningPath: msg.reasoning_path ?? undefined,
                rating: msg.rating,
                followUpQuestions: msg.follow_up_questions,
                chartData: msg.chart_data, // Store for footer info display
                correlationId: msg.correlation_id || undefined,
              },
            }
          }
        )

        setMessages(loadedMessages)
        const lastAssistantAgent = loadedMessages
          .findLast((m) => m.role === 'assistant' && m.metadata?.agentUsed)?.metadata
          ?.agentUsed
        if (lastAssistantAgent) {
          updateConversationActiveAgent(convId, lastAssistantAgent)
          setActiveAgent(lastAssistantAgent)
        }
        lastLoadedConversationRef.current = convId
      } catch (err) {
        // Ignore abort errors (expected when switching conversations quickly)
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        console.error('Failed to load conversation messages:', err)
        // Don't set error here as it might be a new conversation
      } finally {
        setIsLoadingMessages(false)
      }
    },
    [setActiveAgent, updateConversationActiveAgent]
  )

  // Cleanup historian simulation on unmount
  useEffect(() => {
    return () => {
      if (historianSimulationRef.current) {
        clearTimeout(historianSimulationRef.current)
        historianSimulationRef.current = null
      }
    }
  }, [])

  // Sync conversation ID and load messages
  useEffect(() => {
    if (initialConversationId) {
      // If conversationIdRef already matches, we're already in this conversation (just created it)
      // Don't reload messages in this case
      if (conversationIdRef.current === initialConversationId) {
        return
      }

      conversationIdRef.current = initialConversationId
      setCurrentConversationId(initialConversationId)

      // Only load messages if this is a different conversation
      if (lastLoadedConversationRef.current !== initialConversationId) {
        loadConversationMessages(initialConversationId)
      }
    }
  }, [initialConversationId, setCurrentConversationId, loadConversationMessages])

  /**
   * Update feedback on a message (for local state update after API call)
   */
  const updateMessageFeedback = useCallback(
    (messageId: string, rating: number | undefined) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? { ...msg, metadata: { ...msg.metadata, rating } }
            : msg
        )
      )
    },
    []
  )

  /**
   * Navigate between response versions for a given user message.
   * Swaps the displayed assistant message in messages[].
   */
  const navigateVersion = useCallback(
    (userMsgId: string, direction: 'prev' | 'next') => {
      if (isLoading) return

      const entry = responseVersions.get(userMsgId)
      if (!entry) return

      const totalVersions = entry.responses.length + 1 // archived + live
      const newIndex =
        direction === 'prev'
          ? Math.max(0, entry.currentIndex - 1)
          : Math.min(totalVersions - 1, entry.currentIndex + 1)

      if (newIndex === entry.currentIndex) return

      const msgs = messagesRef.current
      // Find the user message and the assistant message right after it
      const userIdx = msgs.findIndex((m) => m.id === userMsgId)
      if (userIdx === -1) return
      const assistantIdx = userIdx + 1
      if (assistantIdx >= msgs.length || msgs[assistantIdx].role !== 'assistant') return

      const currentDisplayed = msgs[assistantIdx]
      const liveIndex = entry.responses.length // index that means "live"

      setResponseVersions((prev) => {
        const next = new Map(prev)
        const existing = next.get(userMsgId)
        if (!existing) return prev // entry was cleared (e.g. conversation changed)
        const e = { ...existing, responses: [...existing.responses] }

        // Leaving live view → save the current live message
        if (entry.currentIndex === liveIndex && newIndex !== liveIndex) {
          e.liveMessage = currentDisplayed
        }

        e.currentIndex = newIndex
        next.set(userMsgId, e)
        return next
      })

      // Determine which message to show
      const messageToShow =
        newIndex === liveIndex
          ? (entry.liveMessage ?? currentDisplayed) // restoring live
          : entry.responses[newIndex]

      setMessages((prev) =>
        prev.map((msg, idx) => (idx === assistantIdx ? messageToShow : msg))
      )
    },
    [isLoading, responseVersions]
  )

  /**
   * Get version info for an assistant message.
   * Returns null if no versions exist.
   */
  const getVersionInfo = useCallback(
    (assistantMessage: UIMessage): VersionInfo | null => {
      if (assistantMessage.role !== 'assistant') return null

      const msgs = messagesRef.current
      const assistantIdx = msgs.findIndex((m) => m.id === assistantMessage.id)
      if (assistantIdx <= 0) return null

      // Find the user message right before
      const userMsg = msgs[assistantIdx - 1]
      if (!userMsg || userMsg.role !== 'user') return null

      const entry = responseVersions.get(userMsg.id)
      if (!entry || entry.responses.length === 0) return null

      const totalVersions = entry.responses.length + 1
      return {
        currentVersion: entry.currentIndex + 1, // 1-based
        totalVersions,
        userMessageId: userMsg.id,
      }
    },
    [responseVersions]
  )

  return {
    // Messages
    messages,

    // Input state
    input,
    setInput,

    // Actions
    send,
    reload,
    stop,
    reset,
    editMessage,
    updateMessageFeedback,
    // FS-676: answer an egress-consent prompt (approve/deny card).
    respondToConsent,

    // Status
    isLoading,
    isLoadingMessages,
    error,

    // Agent
    activeAgent,
    setActiveAgent,

    // Conversation
    conversationId: conversationIdRef.current,

    // Orchestration
    orchestrationStatus,
    // W16 / TIME-198: ephemeral tool-progress status. Null when no
    // long-running tool is in flight; cleared on terminal status.
    toolProgress,

    // Version history
    navigateVersion,
    getVersionInfo,
  }
}

export default useChatV2
